import { Link } from "react-router-dom";
import DoospLayout from "@/components/doosp/DoospLayout";
import { PROGRAMS } from "@/lib/programs";
import { ArrowRight, ShieldAlert, ShieldCheck, HeartPulse, Flame, Biohazard, ArrowUpRight, Sparkles } from "lucide-react";

const ICONS = { ShieldAlert, ShieldCheck, HeartPulse, Flame, Biohazard };

export default function Programs() {
  return (
    <DoospLayout>
      <section className="border-b border-zinc-200 bg-gradient-to-b from-white to-zinc-50">
        <div className="max-w-5xl mx-auto px-6 py-24">
          <div className="text-xs font-mono uppercase tracking-widest text-red-800 mb-4">Occupational safety programs</div>
          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]" data-testid="programs-heading">
            Certification programs<br />
            <span className="text-red-800">for safety-critical work.</span>
          </h1>
          <p className="text-lg text-zinc-600 mt-6 max-w-3xl leading-relaxed">
            Five focused programs designed around the highest-risk exposures in healthcare and
            adjacent industries. Each program is standards-aligned, practitioner-reviewed, and
            keeps your credential current for two years.
          </p>
        </div>
      </section>

      <section className="border-b border-zinc-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-20 space-y-8">
          {PROGRAMS.map((p) => {
            const Icon = ICONS[p.icon] || ShieldCheck;
            const isLive = p.status === "live";
            return (
              <Link
                key={p.slug}
                to={`/programs/${p.slug}`}
                className={`group grid md:grid-cols-12 gap-8 items-center rounded-2xl border-2 p-8 md:p-10 transition ${
                  isLive ? "border-red-600 bg-white hover:shadow-2xl" : "border-zinc-200 bg-white hover:border-zinc-400 hover:shadow-xl"
                }`}
                data-testid={`programs-item-${p.slug}`}
              >
                <div className="md:col-span-4">
                  <div className="rounded-lg overflow-hidden border border-zinc-200">
                    <img src={p.heroImage} alt={p.name} className="w-full h-40 object-cover group-hover:scale-105 transition duration-500" />
                  </div>
                </div>
                <div className="md:col-span-8">
                  <div className="flex items-start gap-4">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-lg bg-${p.color}-50 flex-shrink-0`}>
                      <Icon className={`h-6 w-6 text-${p.color}-600`} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{p.shortName}</span>
                        {isLive ? (
                          <span className="text-[10px] font-mono uppercase tracking-widest bg-red-600 text-white px-2 py-0.5 rounded">Live</span>
                        ) : (
                          <span className="text-[10px] font-mono uppercase tracking-widest bg-zinc-100 text-zinc-500 px-2 py-0.5 rounded">Coming soon</span>
                        )}
                      </div>
                      <div className="font-display font-bold text-2xl md:text-3xl leading-tight mt-1">{p.name}</div>
                      <div className="text-xs text-zinc-500 font-mono mt-1">{p.tagline}</div>
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-zinc-400 group-hover:text-red-600 flex-shrink-0" />
                  </div>
                  <p className="text-sm text-zinc-600 mt-4 leading-relaxed">{p.summary}</p>
                  <div className="flex flex-wrap gap-4 mt-4 text-xs font-mono uppercase tracking-widest text-zinc-500">
                    <span>{p.modules} modules</span>
                    <span>·</span>
                    <span>{p.durationLabel}</span>
                    <span>·</span>
                    <span>${p.priceUsd} USD</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      <section className="bg-zinc-900 text-white">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <Sparkles className="h-8 w-8 text-red-400 mx-auto mb-4" strokeWidth={1.5} />
          <h2 className="font-display text-3xl md:text-4xl font-bold">Don&rsquo;t see the certification you need?</h2>
          <p className="text-zinc-400 mt-3 max-w-2xl mx-auto">
            We&rsquo;re actively developing additional programs. Tell us what your team requires and we&rsquo;ll notify you at launch.
          </p>
          <Link to="/contact" className="mt-6 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white h-11 px-6 rounded-md text-sm font-medium">
            Request a program <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </DoospLayout>
  );
}
